import React, { useEffect, useMemo, useRef, useState } from "react";

export default function Reviews() {
  // ✅ 9 reviews = 3 pages (tabs) on desktop (3 per page)
  const reviews = [
    {
      name: "Omar K",
      text: "Vials arrived clean, sealed, and clearly labeled. Batch consistency across products made reordering easy.",
      rating: 5,
    },
    {
      name: "Priya D",
      text: "Packaging felt lab-grade and professional. Everything was organized, clean, and confidence-inspiring.",
      rating: 5,
    },
    {
      name: "Daniel P",
      text: "Fast handling and consistent quality between orders. Labels and dosing clarity were exactly what I wanted.",
      rating: 5,
    },
    {
      name: "Sofia A",
      text: "Noticed steady, consistent results over time. The product pages and consistency helped me stay on track.",
      rating: 5,
    },
    {
      name: "Hassan E",
      text: "Smooth experience from browsing to checkout. Everything arrived in great condition and looked premium.",
      rating: 5,
    },
    {
      name: "Amara O",
      text: "Results felt consistent across weeks. Clean packaging and clear labeling gave me confidence.",
      rating: 5,
    },
    {
      name: "Ethan M",
      text: "Quality control feels consistent—same presentation, same clarity each order. Very reliable experience overall.",
      rating: 5,
    },
    {
      name: "Yara N",
      text: "Clean seals, clear labels, and a professional look. The consistency between items stood out immediately.",
      rating: 5,
    },
    {
      name: "Min J",
      text: "Everything arrived organized and protected. The overall quality and presentation feels very consistent.",
      rating: 5,
    },
  ];

  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Header />
        <ScrollCarousel reviews={reviews} />
      </div>
    </section>
  );
}

function Header() {
  return (
    <div className="flex flex-col gap-3">
<h2 className="mt-3 mb-10 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl text-center">
  Customer Reviews
</h2>

    </div>
  );
}

/**
 * Minimal carousel:
 * - auto-rotates every 8s
 * - no top controls
 * - only bottom "tabs" (dots)
 * - hidden scrollbar, snap pages
 */
function ScrollCarousel({ reviews }) {
  const scrollerRef = useRef(null);
  const intervalRef = useRef(null);

  const [perView, setPerView] = useState(3);
  const [activePage, setActivePage] = useState(0);

  const AUTO_MS = 8000;

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) return 1;
      if (w < 1024) return 2;
      return 3;
    };

    const onResize = () => setPerView(compute());
    setPerView(compute());

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const pages = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(reviews.length / perView));
    return Array.from({ length: totalPages }, (_, i) => i);
  }, [reviews.length, perView]);

  function goToPage(i) {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  }

  function handleScroll() {
    const el = scrollerRef.current;
    if (!el) return;

    const pageWidth = el.clientWidth;
    const idx = Math.round(el.scrollLeft / pageWidth);
    setActivePage(Math.min(Math.max(0, idx), pages.length - 1));
  }

  // ✅ single interval source of truth
  function startAuto() {
    if (pages.length <= 1) return;

    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActivePage((prev) => {
        const next = (prev + 1) % pages.length;
        goToPage(next);
        return next;
      });
    }, AUTO_MS);
  }

  function stopAuto() {
    if (intervalRef.current) clearInterval(intervalRef.current);
  }

  useEffect(() => {
    startAuto();
    return () => stopAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages.length]);

  // If perView changes, keep page in range + snap to current page
  useEffect(() => {
    const clamped = Math.min(activePage, pages.length - 1);
    setActivePage(clamped);
    // snap after layout change
    setTimeout(() => goToPage(clamped), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perView]);

  return (
    <div className="mt-10">
      {/* Track */}
      <div
        className="relative"
        onMouseEnter={stopAuto}
        onMouseLeave={startAuto}
      >

        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className={[
            "flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4",
            "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          ].join(" ")}
        >
          {pages.map((pageIdx) => (
            <div key={pageIdx} className="min-w-full snap-start">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {reviews
                  .slice(pageIdx * perView, pageIdx * perView + perView)
                  .map((r) => (
                    <ReviewCard key={`${r.name}-${r.text.slice(0, 12)}`} r={r} />
                  ))}

                {/* keep desktop grid alignment if last page is short */}
                {perView === 3 &&
                  reviews.slice(pageIdx * perView, pageIdx * perView + perView).length < 3 &&
                  Array.from({
                    length:
                      3 -
                      reviews.slice(pageIdx * perView, pageIdx * perView + perView).length,
                  }).map((_, i) => (
                    <div key={`spacer-${pageIdx}-${i}`} className="hidden lg:block" />
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ✅ Only bottom tabs (dots). No progress bar. */}
      {pages.length > 1 && (
        <div className="mt-5 mb-6 flex items-center justify-center gap-2">
          {pages.map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                stopAuto();
                goToPage(i);
                setActivePage(i);
                startAuto();
              }}
              className={[
                "h-2.5 rounded-full transition-all",
                i === activePage ? "w-8 bg-slate-900" : "w-2.5 bg-slate-300 hover:bg-slate-400",
              ].join(" ")}
              aria-label={`Go to page ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewCard({ r }) {
  const rating = Math.max(1, Math.min(5, r.rating ?? 5));

  return (
    <div className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg min-h-[280px] sm:min-h-[320px]
 flex flex-col">
      {/* Stars (centered) */}
      <div className="flex justify-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <GoldStar key={i} muted={i >= rating} />
        ))}
      </div>

      {/* Quote (centered) */}
      <p className="mt-6 text-sm leading-relaxed text-slate-700 text-center flex-1">
        <span className="text-lg font-semibold text-slate-900">“</span>
        {r.text}
        <span className="text-lg font-semibold text-slate-900">”</span>
      </p>

      {/* Writer (centered) */}
      <div className="mt-8 text-center">
        <div className="text-sm font-semibold text-slate-900">{r.name}</div>
      </div>
    </div>
  );
}

function GoldStar({ muted = false }) {
  return (
    <svg
      className={["h-5 w-5", muted ? "text-slate-300" : "text-yellow-500"].join(" ")}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}
